import React, { useState, useRef, memo } from 'react';
import { CKEditor } from 'ckeditor4-react';
import { Api } from '../Service/api';
import { PencilIcon } from '@heroicons/react/24/outline';

/**
 * EditableQuestion Component
 * 
 * A reusable component that displays question content with edit functionality.
 * Supports CKEditor with MathJax, image uploads, and multi-language text (Gujarati, Hindi).
 * 
 * @param {string} value - Current question HTML content
 * @param {function} onChange - Callback function to update question
 * @param {number} overAllFontSize - Font size for display mode
 * @param {string} editorUrl - CKEditor CDN URL
 * @param {object} style - Additional styles for the container
 * @param {string} className - Additional CSS classes
 */
const EditableQuestion = memo(({
    value,
    finalquestion,
    question,
    onChangefinalQuestion,
    onChangeQuestion,
    overAllFontSize = 18,
    editorUrl = "https://cdn.ckeditor.com/4.22.1/full-all/ckeditor.js",
    className = ""
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState(question);
    const [isLoading, setIsLoading] = useState(false);
    const editorRef = useRef(null);

    const handleEdit = () => {
        setTempValue(question);
        setIsEditing(true);
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const res = await Api.ConvertLaxTaXIntoMathMl({ html: tempValue });
            if (res.statusCode === 200) {
                const data = JSON.parse(res.data);
                const convertedHtml = data?.data || data;
                onChangefinalQuestion(convertedHtml);
                onChangeQuestion(tempValue)
                setIsEditing(false);
            } else {
                console.error("API Error", res);
                onChangefinalQuestion(tempValue);
                setIsEditing(false);
            }
        } catch (error) {
            console.error("Error converting HTML", error);
            onChangefinalQuestion(tempValue);
            setIsEditing(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setTempValue(question);
        setIsEditing(false);
    };

    const handleEditorChange = (event) => {
        setTempValue(event.editor.getData());
    };

    if (isEditing) {
        return (
            <div className={`editable-question-container ${className}`}>
                <div className="ckeditor-wrapper">
                    <CKEditor
                        editorUrl={editorUrl}
                        onInstanceReady={(instance) => {
                            editorRef.current = instance;
                        }}
                        initData={tempValue || ''}
                        onChange={handleEditorChange}
                        config={{
                            extraPlugins: [
                                "mathjax",
                                "uploadimage",
                            ],
                            removeDialogTabs: "image:advanced;link:advanced",
                            mathJaxLib: "https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.4/MathJax.js?config=TeX-AMS_HTML",
                            autoParagraph: false,
                            filebrowserImageBrowseUrl: "/imageupload",
                            versionCheck: false,
                            height: 200,
                        }}
                    />
                </div>
                <div className="editor-actions" style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                    <button
                        onClick={handleSave}
                        className="save-btn"
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            fontSize: '14px'
                        }}
                    >
                        Save
                    </button>
                    <button
                        onClick={handleCancel}
                        className="cancel-btn"
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            fontSize: '14px'
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`editable-question-view ${className}`} style={{ position: 'relative' }}>
            <div
                style={{ fontWeight: "500", fontSize: `${overAllFontSize}px` }}
                dangerouslySetInnerHTML={{ __html: finalquestion || '' }}
                className="table-container"
            />
            <button
                onClick={handleEdit}
                className="edit-btn"
                style={{
                    marginTop: '8px',
                    padding: '6px 8px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: "fit-content",
                    borderRadius: '30%',
                    opacity: isLoading ? 0.6 : 1,
                    cursor: isLoading ? "not-allowed" : "pointer",
                }}
            >
                <PencilIcon style={{ width: '14px', height: '14px' }} />
            </button>
        </div>
    );
});

EditableQuestion.displayName = 'EditableQuestion';

export default EditableQuestion;
